import { PerformanceCalculationJob } from "components/forecasting/hooks/types/enqueue.types";

// base chart-style settings
export const WellSurveyAccess = "Survey";
export const FracAccess = "Frac";
export const cookies = {
  LAST_LOGGED_IN: "last-logged-in",
  CUSTOM_COLORS: "custom-colors",
  LOGIN_EMAIL: "login-email",
  GEOSCOUT_STATUS: "geoscout-status"
};

export const MAX_RECENT_COLORS = 10;

export const MY_FOCUS_FIELDS = "My Fields";
export const ORG_FOCUS_FIELDS = "Organization Fields";
export const PROJECT_FOCUS_FIELDS = "Project Fields";
export const SHAPEFILE_FIELDS = "Shapefile & GeoTIFF";
export const ORG_GEO_FIELDS = "Organization Calculated Geo Fields";
export const ORG_GEO_SIMS = "Organization Geo Simulations";
export const MCDANIEL_SEARCH_FOLDER_NAME = "McDaniel Research";
export const WORKING_FORECAST_FOLDER_NAME = "Working Forecasts";
export const WORKING_FORECAST_SOURCE = "forecastWorking";
export const MCDANIEL_NAME = "McDaniel";
export const NOVI_SEARCH_FOLDER_NAME = "Novi";
export const NOVI_FOLDER_NAME = "Novi Labs";
export const NOVI_FORECAST_SOURCE = "shaleProfileDB";
export const FORECAST_KEY = "fcst";
/**
 * Placeholder folder name the synced-database forecast is sent and labelled with in Forecast
 * Compare + sync mode. The chart service echoes it back as the series' `forecastSource`
 * (`getForecastSourceFromLabel` reads it out of the " Forecast (Sync DB)" label), and it
 * defaults the same string server-side when the client sends no folder. It is a placeholder,
 * not a name anyone chose, so the UI resolves it to the real schema before display —
 * see ForecastCompareLegend. (EVA-113)
 */
export const SYNC_DB_FOLDER_NAME = "Sync DB";
export const SYNC_KEY = "sync";
export const DATE_RANGE_KEY = "dr";
export const PDEN_SOURCE_KEY = "pden";
export const FORECAST_TRUMPING_FOLDER_ID = "-3";
export const FORECAST_TRUMPING_FOLDER_NAME = "Forecast Trumping";

/**
 * True when a stored forecast-source value means Forecast Trumping, in either spelling.
 *
 * Two writers disagree, and both are load-bearing. Field-list items are stamped with the id
 * ("-3") by useFieldsWithLabels, while updateGroupBy stamps the display name. A per-chart
 * focus carries the field item straight through without passing updateGroupBy, so a reader
 * that only checks one spelling silently misses the other — and the consequence is a property
 * string qualified for trumping with no forecastTrumpingSettings attached to go with it.
 * Read through this rather than comparing directly.
 */
export const isForecastTrumpingSource = (value: Maybe<string>) =>
  value === FORECAST_TRUMPING_FOLDER_ID || value === FORECAST_TRUMPING_FOLDER_NAME;
export const USER_ARPS = "USERARPS";
export const TYPE_WELLS = "TYPEWELLS";
export const ValNavTypeWellFolderName = "ValNav~~~";
export type ForecastActivityType = typeof USER_ARPS | typeof TYPE_WELLS;
export const NOVI_FOLDER_ID = "-2";
export const MCDANIEL_FOLDER_ID = "-1";
export const WORKING_FORECAST_FOLDER_ID = "-1.5";
// "Both on" (EVA Research + Novi Labs) as a single value of the defaultForecastSource
// setting. Distinct from FORECAST_TRUMPING_FOLDER_ID ("-3") on purpose — trumping is
// gated on both that id and its name elsewhere, and "-4" matches neither.
export const SYSTEM_DEFAULT_FORECAST_FOLDER_ID = "-4";
export const X_MILES_LOCAL_STORAGE_KEY = "xMiles";

// Shared constants for forecasting jobData configuration
export const FORECAST_JOB_DEFAULTS = {
  collection: "pden",
  forecast_coll: WORKING_FORECAST_SOURCE,
  is_user_forecast: false,
  delete: false,
  forecast_source: "working",
  sync_schema: WORKING_FORECAST_SOURCE
};

export const getWorkingPerformanceCalcJobs = (orgId: string) => {
  const orgDb = `org-${orgId}DB`;
  return {
    jobs: [
      {
        ...FORECAST_JOB_DEFAULTS,
        db: "mcdanDB",
        destination_db: orgDb,
        forecast_db: orgDb
      }, //pden from mcdanDB
      {
        ...FORECAST_JOB_DEFAULTS,
        db: "gdcDB",
        destination_db: orgDb,
        forecast_db: orgDb
      }, //pden from gdcDB
      {
        ...FORECAST_JOB_DEFAULTS,
        db: "shaleProfileDB",
        destination_db: orgDb,
        forecast_db: orgDb
      } //pden from gdcDB
    ]
  } as PerformanceCalculationJob;
};

export const FORECAST_FOLDER_ID_TO_FORECAST_SOURCE_MAP: Record<string, string> = {
  [NOVI_FOLDER_ID]: NOVI_FORECAST_SOURCE,
  [MCDANIEL_FOLDER_ID]: MCDANIEL_SEARCH_FOLDER_NAME,
  [WORKING_FORECAST_FOLDER_ID]: WORKING_FORECAST_SOURCE,
  [FORECAST_TRUMPING_FOLDER_ID]: FORECAST_TRUMPING_FOLDER_ID,
  // System Default means "both sources", and this map is single-valued, so it resolves to EVA
  // Research — exactly what a "-1" user gets. Trumping for that cohort is not driven from here:
  // the saved value puts both sources in the Forecasts panel, and the panel-derived value is
  // what turns trumping on. Mapping this entry to FORECAST_TRUMPING_FOLDER_ID would also fire
  // in Compare mode, where the two sources are shown side by side rather than layered.
  [SYSTEM_DEFAULT_FORECAST_FOLDER_ID]: MCDANIEL_SEARCH_FOLDER_NAME
};

export const COLOR_PALETTES = [
  {
    name: "Cold To Hot",
    id: "-1",
    colors: [
      "rgb(41, 204, 148)",
      "rgb(41, 84, 204)",
      "rgb(147, 41, 204)",
      "rgb(204, 41, 57)"
    ]
  },
  {
    name: "Viridis",
    id: "-3",
    colors: ["rgb(68,1,84)", "rgb(54,90,140)", "rgb(40,174,127)", "rgb(233,228,39)"]
  },
  {
    name: "Cool",
    id: "-3",
    colors: [
      "rgb(0,255, 255)",
      "rgb(72, 183, 255)",
      "rgb(164, 91, 255)",
      "rgb(247, 7, 255)"
    ]
  },
  {
    name: "Hot",
    id: "-3",
    colors: ["rgb(10, 0, 0)", "rgb(209, 0, 0)", "rgb(255, 175, 0)", "rgb(255, 255, 193)"]
  },
  {
    name: "Jet",
    id: "-3",
    colors: ["rgb(0,0,127)", "rgb(0, 160, 255)", "rgb(234, 243, 13)", "rgb(159, 7, 0)"]
  },
  {
    name: "Portland",
    id: "-3",
    colors: ["rgb(12,51,131)", "rgb(10,136,186)", "rgb(242,143,56)", "rgb(217,30,30)"]
  }
];

export const COMPATIBLE_COLORMAP_NAMES = [
  "viridis",
  "plasma",
  "inferno",
  "jet",
  "hot",
  "spring",
  "portland",
  "autumn",
  "rainbow",
  "bluered",
  "rdbu"
  // Below are compatible colormap names that exist in the colormap npm package,
  // but are not included in our default COLOR_PALETTES.
  // "magma",
  // "cividis",
  // "turbo",
  // "hsv",
  // "summer",
  // "winter",
  // "bone",
  // "copper",
  // "greys",
  // "greens",
  // "blues",
  // "reds",
  // "pink"
];

export const base_chart_style = {
  wellStyles: [
    {
      color: 4280629062,
      title: "Well/Group 1",
      dashed: false,
      thickness: 1.25
    },
    {
      color: 4288986306,
      title: "Well/Group 2",
      dashed: false,
      thickness: 1.25
    },
    {
      color: 4283651674,
      title: "Well/Group 3",
      dashed: false,
      thickness: 1.25
    },
    {
      color: 4289117655,
      title: "Well/Group 4",
      dashed: false,
      thickness: 1.25
    },
    {
      color: 4292066445,
      title: "Well/Group 5",
      dashed: false,
      thickness: 1.25
    },
    {
      color: 4291732815,
      title: "Well/Group 6",
      dashed: false,
      thickness: 1.25
    },
    {
      color: 4290085200,
      title: "Well/Group 7",
      dashed: false,
      thickness: 1.25
    },
    {
      color: 4292642355,
      title: "Well/Group 8",
      dashed: false,
      thickness: 1.25
    },
    {
      color: 4288959048,
      title: "Well/Group 9",
      dashed: false,
      thickness: 1.25
    },
    {
      color: 4292462750,
      title: "Well/Group 10",
      dashed: false,
      thickness: 1.25
    },
    {
      color: 4288330157,
      title: "Well/Group 11",
      dashed: false,
      thickness: 1.25
    },
    {
      color: 4288170070,
      title: "Well/Group 12",
      dashed: false,
      thickness: 1.25
    },
    {
      color: 4289931345,
      title: "Well/Group 13",
      dashed: false,
      thickness: 1.25
    },
    {
      color: 4286519309,
      title: "Well/Group 14",
      dashed: false,
      thickness: 1.25
    },
    {
      color: 4287867392,
      title: "Well/Group 15",
      dashed: false,
      thickness: 1.25
    },
    {
      color: 4284035861,
      title: "Well/Group 16",
      dashed: false,
      thickness: 1.25
    },
    {
      color: 4293999123,
      title: "Well/Group 17",
      dashed: false,
      thickness: 1.25
    },
    {
      color: 4280495126,
      title: "Well/Group 18",
      dashed: false,
      thickness: 1.25
    }
  ],
  scatterSize: 8,
  averageStyle: {
    color: 4294052899,
    title: "Average",
    dashed: false,
    thickness: 3
  },
  selectedStyle: {
    color: 4278190080,
    title: "Selected well",
    dashed: false,
    thickness: 3
  },
  notAvailableStyle: {
    color: 4292269782,
    title: "N/A",
    dashed: false,
    thickness: 1.25
  },
  referenceCurvedStyles: [
    {
      title: "Reference Curve 1",
      color: 4283914071,
      dashed: false,
      thickness: 3
    },
    {
      title: "Reference Curve 2",
      color: 4283914071,
      dashed: false,
      thickness: 3
    },
    {
      title: "Reference Curve 3",
      color: 4283914071,
      dashed: false,
      thickness: 3
    },
    {
      title: "Reference Curve 4",
      color: 4283914071,
      dashed: false,
      thickness: 3
    },
    {
      title: "Reference Curve 5",
      color: 4283914071,
      dashed: false,
      thickness: 3
    },
    {
      title: "Reference Curve 6",
      color: 4283914071,
      dashed: false,
      thickness: 3
    }
  ]
};
export const screenShotSizes = {
  HR1: { width: 1152, height: 681 },
  S1: { width: 1184, height: 938 },
  HR2: { width: 2416, height: 956 },
  VR1: { width: 774, height: 938 }
};

// thickness items for chart-style settings
export const thickness_items_list = [
  { label: "Thin", thickness: 0.75, dashed: false },
  { label: "Normal", thickness: 1.25, dashed: false },
  { label: "Medium", thickness: 2, dashed: false },
  { label: "Semi Bold", thickness: 3, dashed: false },
  { label: "Bold", thickness: 4, dashed: false },
  { label: "Extra Bold", thickness: 5, dashed: false },
  { label: "Heavy", thickness: 6, dashed: false },
  { label: "Thin", thickness: 0.75, dashed: true },
  { label: "Normal", thickness: 1.25, dashed: true },
  { label: "Medium", thickness: 2, dashed: true },
  { label: "Semi Bold", thickness: 3, dashed: true },
  { label: "Bold", thickness: 4, dashed: true },
  { label: "Extra Bold", thickness: 5, dashed: true },
  { label: "Heavy", thickness: 6, dashed: true }
];

// scatter-size list for chart-style settings
export const scatter_size_list = [
  { label: "Thin", size: 2 },
  { label: "Normal", size: 4 },
  { label: "Medium", size: 6 },
  { label: "Semi Bold", size: 8 },
  { label: "Bold", size: 10 },
  { label: "Extra Bold", size: 12 },
  { label: "Heavy", size: 14 }
];
